import { parse as parseSDF } from 'sdf-parser';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { makeOneSDF } from 'BMapsUtil/mol_format_utils';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';

/**
 * DrugFlow structure-based de novo generation job: parses the generated
 * molecules from the result SDF into compound structures.
 *
 * Like GninaDockingJob, results are SDF molblocks (no SMILES column), so we
 * parse with sdf-parser and carry each molblock as the structure. SMILES is
 * derived on import when available.
 *
 * @augments JobDefinition
 */
export class DrugFlowJob extends JobDefinition {
    static jobTypeId = JobTypes.DRUGFLOW;
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'DrugFlow';
    static jobName = 'DrugFlow Job';

    /** Results rows keyed by their generated id. */
    static idField = 'id';

    /**
     * Results-table columns for generated compounds.
     * @override
     * @returns {Object<string, import('../JobDefinition').DataInfoEntry>}
     */
    static getDataInfo() {
        return {
            SMILES: {
                label: 'Structure', structureValue: true, defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
            name: {
                label: 'Name', type: 'string', defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
        };
    }

    /**
     * Parses the DrugFlow SDF into generated structures and stores them on
     * local job state for the results table / import.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        let resultsPacket = results;
        let structures = [];
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket.jobResults?.sdf) return { error: 'Empty results' };
            const parsed = parseSDF(resultsPacket.jobResults.sdf);
            structures = parsed.molecules.map((entry, i) => {
                const name = `drugflow_${i + 1}`;
                const sdf = makeOneSDF(entry.molfile, { tmpSpec: name, tmpI: i });
                return {
                    id: `${job.jobid}_${i}`,
                    name,
                    SMILES: entry.smiles || null,
                    sdf,
                };
            });
            updateLocalJobState(job.jobid, { structures });
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return {
            error,
            results: { structures, jobResults: resultsPacket.jobResults },
        };
    }
}
